import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  es: {
    // Navigation
    nav: {
      services: 'Soluciones',
      products: 'Productos',
      clients: 'Ecosistema & Casos',
      process: 'Proceso',
      contact: 'Contacto',
      schedule: 'Agendar Reunión'
    },
    // Hero
    hero: {
      badge: 'Innovación & Automatización Digital',
      title: 'Diseñamos y escalamos tus',
      titleHighlight: 'Productos Digitales & IA',
      subtitle: 'Dejamos atrás la complejidad técnica. Creamos plataformas SaaS, apps móviles y automatizaciones inteligentes que transforman operaciones y multiplican los resultados de tu empresa.',
      cta: 'Agendar una Reunión',
      ctaSecondary: 'Explorar Soluciones',
      stats: {
        projects: 'Productos Lanzados',
        clients: 'Clientes Satisfechos',
        years: 'Años de Experiencia'
      },
      highlights: [
        'Prototipos interactivos en días',
        'Automatizaciones y agentes IA 24/7',
        'Time-to-market acelerado'
      ]
    },
    // Services
    services: {
      badge: 'Soluciones de Alto Impacto',
      title: 'Soluciones Estratégicas para',
      titleHighlight: 'Impulsar tu Negocio',
      subtitle: 'No vendemos horas de código. Diseñamos productos digitales completos y automatizaciones que resuelven cuellos de botella operativos y generan rentabilidad.',
      core: {
        products: {
          title: 'Creación de Productos Digitales & SaaS',
          description: 'De la idea al producto final listo para el mercado. Diseñamos y desarrollamos plataformas web, portales de clientes y soluciones SaaS escalables con experiencia de usuario excepcional.',
          tags: ['SaaS', 'Web Apps', 'Escalabilidad', 'Arquitectura Cloud']
        },
        automation: {
          title: 'Automatización Inteligente & Agentes IA',
          description: 'Optimizamos tus operaciones conectando sistemas y desplegando agentes autónomos de IA que trabajan 24/7 para reducir tareas manuales y costos operativos.',
          tags: ['Flujos IA', 'Agentes Autónomos', 'Integraciones', 'Procesamiento 24/7']
        },
        platforms: {
          title: 'Apps Móviles & E-commerce de Conversión',
          description: 'Experiencias móviles nativas y multiplataforma (iOS & Android) junto a canales de comercio digital diseñados para maximizar retención y ventas.',
          tags: ['iOS & Android', 'E-commerce', 'Checkout Optimizado', 'Push & Realtime']
        },
        innovation: {
          title: 'Estrategia, UX & Prototipado Rápido',
          description: 'Validamos tu modelo de negocio antes de construir. Diseñamos prototipos interactivos de alta fidelidad para testear con usuarios y levantar capital sin desperdicio.',
          tags: ['Prototipos Rápidos', 'Diseño UX/UI', 'Validación MVP', 'Estrategia Digital']
        }
      },
      additional: {
        cloud: {
          title: 'Infraestructura Cloud & DevOps',
          description: 'Despliegues continuos, alta disponibilidad y arquitectura cloud moderna diseñada para escalar según la demanda de tu negocio.'
        },
        analytics: {
          title: 'Analítica Predictiva & Dashboards',
          description: 'Métricas clave en tiempo real e inteligencia de negocio para decisiones fundamentadas.'
        },
        security: {
          title: 'Seguridad & Protección de Datos',
          description: 'Auditorías preventivas, cifrado de extremo a extremo y mejores prácticas de compliance.'
        },
        support: {
          title: 'Evolución Continua & Soporte 24/7',
          description: 'Acompañamiento continuo, mantenimiento preventivo y optimización post-lanzamiento.'
        }
      }
    },
    // Clients / Products
    clients: {
      badge: 'Ecosistema de Productos & Casos',
      title: 'Productos Propios y Plataformas que',
      titleHighlight: 'Transforman Empresas',
      subtitle: 'Diseñamos y operamos productos SaaS propios como Catálogo Hub y Vegsoft Barber, además de arquitecturas de software a medida para empresas líderes.',
      filterAll: 'Todos los Productos',
      filterSaaS: 'SaaS Vegsoft',
      filterEcommerce: 'E-commerce & Logística',
      filterEnterprise: 'Operaciones & B2B'
    },
    // Process
    process: {
      badge: 'Metodología Ágil',
      title: 'De la Idea al Producto Operativo en',
      titleHighlight: '4 Pasos Clave',
      subtitle: 'Un camino claro, transparente y enfocado en validar valor desde la primera semana.',
      steps: {
        discovery: {
          title: 'Conversación & Estrategia',
          description: 'Conversamos sobre tus procesos, objetivos y metas comerciales para definir la hoja de ruta óptima.'
        },
        prototype: {
          title: 'Prototipo Interactivo en Días',
          description: 'Diseñamos la interfaz interactiva para que veas y valides el producto antes de invertir en desarrollo.'
        },
        development: {
          title: 'Construcción & Automatización',
          description: 'Desarrollo modular con entregas continuas, integración de IA y testing automatizado de alta calidad.'
        },
        launch: {
          title: 'Lanzamiento & Escalamiento',
          description: 'Despliegue seguro en la nube, capacitación y acompañamiento para escalar usuarios e ingresos.'
        }
      }
    },
    // Contact
    contact: {
      badge: 'Inicia tu Proyecto',
      title: 'Hablemos de tu',
      titleHighlight: 'Próxima Solución Digital',
      subtitle: 'Cuéntanos sobre tu negocio y te presentaremos una propuesta con prototipo inicial sin compromiso.',
      form: {
        name: 'Nombre completo',
        email: 'Correo electrónico',
        phone: 'Teléfono / WhatsApp (opcional)',
        company: 'Empresa u Organización (opcional)',
        service: 'Solución de interés',
        selectService: 'Selecciona una solución',
        message: 'Cuéntanos qué producto o proceso deseas crear o automatizar',
        submit: 'Enviar Mensaje',
        sending: 'Enviando...',
        success: '¡Mensaje enviado correctamente! Te contactaremos a la brevedad.',
        error: 'Error al enviar. Intenta nuevamente o contáctanos por WhatsApp.'
      },
      info: {
        email: 'Email Corporativo',
        whatsapp: 'WhatsApp Directo',
        schedule: '¿Prefieres una llamada de 30 minutos?',
        scheduleBtn: 'Agendar una Reunión'
      }
    },
    // Calendar Modal
    calendar: {
      title: 'Agendar una Reunión',
      subtitle: 'Sesión de 30 minutos sin costo para conversar sobre tu proyecto o idea',
      selectDate: 'Selecciona una fecha',
      selectTime: 'Selecciona una hora',
      noTimes: 'No hay horarios disponibles para esta fecha',
      form: {
        name: 'Nombre completo',
        email: 'Correo electrónico',
        phone: 'Teléfono / WhatsApp',
        company: 'Empresa (opcional)',
        service: 'Solución de interés',
        selectService: 'Selecciona una solución',
        notes: 'Detalles clave de tu idea o necesidad (opcional)',
        submit: 'Confirmar Reunión',
        submitting: 'Agendando...',
        success: '¡Reunión agendada con éxito! Recibirás los detalles en tu correo.',
        error: 'Error al agendar. Intenta nuevamente.'
      }
    },
    // Footer
    footer: {
      description: 'Creamos productos digitales de alto impacto, plataformas SaaS y automatizaciones inteligentes que impulsan la rentabilidad y escala de las empresas.',
      services: 'Soluciones',
      contact: 'Contacto',
      rights: 'Todos los derechos reservados.'
    },
    // Common
    common: {
      learnMore: 'Conoce más detalles',
      free: 'SIN COSTO',
      services: {
        catalogoHub: 'Catálogo Hub (Catálogos Digitales & WhatsApp)',
        products: 'Productos Digitales & SaaS',
        barber: 'Vegsoft Barber (SaaS de Citas)',
        automation: 'Automatización & Agentes IA',
        platforms: 'Apps Móviles & E-commerce',
        innovation: 'Estrategia & Prototipado Rápido',
        web: 'Plataformas Web & SaaS',
        mobile: 'Apps Móviles (iOS/Android)',
        ecommerce: 'E-commerce & Canales Digitales',
        ai: 'Automatización con IA',
        workflows: 'Optimización de Procesos',
        consulting: 'Consultoría & Estrategia'
      }
    }
  },
  en: {
    // Navigation
    nav: {
      services: 'Solutions',
      products: 'Products',
      clients: 'Ecosystem & Cases',
      process: 'Process',
      contact: 'Contact',
      schedule: 'Schedule a Call'
    },
    // Hero
    hero: {
      badge: 'Digital Innovation & Automation',
      title: 'We design and scale your',
      titleHighlight: 'Digital Products & AI',
      subtitle: 'We eliminate technical complexity. We create SaaS platforms, mobile apps, and intelligent automations that transform operations and multiply your business results.',
      cta: 'Schedule a Free Call',
      ctaSecondary: 'Explore Solutions',
      stats: {
        projects: 'Products Launched',
        clients: 'Happy Clients',
        years: 'Years of Experience'
      },
      highlights: [
        'Interactive prototypes in days',
        '24/7 AI agents and automations',
        'Accelerated time-to-market'
      ]
    },
    // Services
    services: {
      badge: 'High-Impact Solutions',
      title: 'Strategic Solutions to',
      titleHighlight: 'Scale Your Business',
      subtitle: 'We do not sell coding hours. We build complete digital products and automations that resolve operational bottlenecks and drive real revenue.',
      core: {
        products: {
          title: 'Digital Products & SaaS Creation',
          description: 'From idea to market-ready product. We design and build scalable web platforms, client portals, and SaaS solutions with exceptional user experiences.',
          tags: ['SaaS', 'Web Apps', 'Scalability', 'Cloud Architecture']
        },
        automation: {
          title: 'Intelligent Automation & AI Agents',
          description: 'We streamline your operations by connecting systems and deploying autonomous AI agents that work 24/7 to reduce manual tasks and operating costs.',
          tags: ['AI Workflows', 'Autonomous Agents', 'Integrations', '24/7 Operations']
        },
        platforms: {
          title: 'Mobile Apps & High-Conversion E-commerce',
          description: 'Native and cross-platform mobile experiences (iOS & Android) alongside digital commerce channels designed to maximize customer retention and sales.',
          tags: ['iOS & Android', 'E-commerce', 'Optimized Checkout', 'Realtime Sync']
        },
        innovation: {
          title: 'Strategy, UX & Rapid Prototyping',
          description: 'We validate your business model before building. We create high-fidelity interactive prototypes to test with users and raise capital without wasted budget.',
          tags: ['Rapid Prototyping', 'UX/UI Design', 'MVP Validation', 'Digital Strategy']
        }
      },
      additional: {
        cloud: {
          title: 'Cloud Infrastructure & DevOps',
          description: 'Continuous deployments, high availability, and modern cloud architecture designed to scale on demand.'
        },
        analytics: {
          title: 'Predictive Analytics & Dashboards',
          description: 'Real-time key performance metrics and business intelligence for data-driven decisions.'
        },
        security: {
          title: 'Security & Data Protection',
          description: 'Preventive audits, end-to-end encryption, and top compliance standards.'
        },
        support: {
          title: 'Continuous Evolution & 24/7 Support',
          description: 'Dedicated partnership, preventive maintenance, and post-launch performance optimization.'
        }
      }
    },
    // Clients / Products
    clients: {
      badge: 'Product Ecosystem & Case Studies',
      title: 'Proprietary Products & Platforms that',
      titleHighlight: 'Transform Businesses',
      subtitle: 'We build, launch, and operate high-demand SaaS products like Catálogo Hub and Vegsoft Barber, alongside custom platforms for industry leaders.',
      filterAll: 'All Products',
      filterSaaS: 'Vegsoft SaaS',
      filterEcommerce: 'E-commerce & Logistics',
      filterEnterprise: 'Operations & B2B'
    },
    // Process
    process: {
      badge: 'Agile Methodology',
      title: 'From Idea to Production in',
      titleHighlight: '4 Key Steps',
      subtitle: 'A clear, transparent roadmap focused on validating real business value from week one.',
      steps: {
        discovery: {
          title: 'Discovery & Strategy',
          description: 'We discuss your workflows, goals, and business vision to define the optimal product roadmap.'
        },
        prototype: {
          title: 'Interactive Prototype in Days',
          description: 'We design the interactive interface so you can see and validate the solution before writing code.'
        },
        development: {
          title: 'Building & Automation',
          description: 'Modular agile development with continuous releases, AI integration, and automated quality testing.'
        },
        launch: {
          title: 'Launch & Scaling',
          description: 'Secure cloud deployment, onboarding, and proactive support to scale your users and revenue.'
        }
      }
    },
    // Contact
    contact: {
      badge: 'Start Your Project',
      title: "Let's talk about your",
      titleHighlight: 'Next Digital Solution',
      subtitle: 'Tell us about your business and we will deliver a tailored proposal with an initial prototype preview.',
      form: {
        name: 'Full name',
        email: 'Email address',
        phone: 'Phone / WhatsApp (optional)',
        company: 'Company or Organization (optional)',
        service: 'Solution of interest',
        selectService: 'Select a solution',
        message: 'Tell us what product or process you want to build or automate',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Message sent successfully! We will get in touch shortly.',
        error: 'Error sending. Please try again or reach us directly via WhatsApp.'
      },
      info: {
        email: 'Corporate Email',
        whatsapp: 'Direct WhatsApp',
        schedule: 'Prefer a 30-minute introductory call?',
        scheduleBtn: 'Schedule a Call'
      }
    },
    // Calendar Modal
    calendar: {
      title: 'Schedule a Meeting',
      subtitle: '30-minute free session to discuss your project or automation idea',
      selectDate: 'Select a date',
      selectTime: 'Select a time',
      noTimes: 'No available times for this date',
      form: {
        name: 'Full name',
        email: 'Email address',
        phone: 'Phone / WhatsApp',
        company: 'Company (optional)',
        service: 'Solution of interest',
        selectService: 'Select a solution',
        notes: 'Key details about your idea or challenge (optional)',
        submit: 'Confirm Meeting',
        submitting: 'Scheduling...',
        success: 'Meeting scheduled successfully! Check your email for confirmation.',
        error: 'Error scheduling. Please try again.'
      }
    },
    // Footer
    footer: {
      description: 'We build high-impact digital products, SaaS platforms, and intelligent automations that multiply business profitability and scale.',
      services: 'Solutions',
      contact: 'Contact',
      rights: 'All rights reserved.'
    },
    // Common
    common: {
      learnMore: 'Learn more details',
      free: 'FREE',
      services: {
        catalogoHub: 'Catálogo Hub (Digital Catalogs & WhatsApp)',
        products: 'Digital Products & SaaS',
        barber: 'Vegsoft Barber (Booking SaaS)',
        automation: 'Automation & AI Agents',
        platforms: 'Mobile Apps & E-commerce',
        innovation: 'Strategy & Rapid Prototyping',
        web: 'Web Platforms & SaaS',
        mobile: 'Mobile Apps (iOS/Android)',
        ecommerce: 'E-commerce & Digital Channels',
        ai: 'AI Automation',
        workflows: 'Process Optimization',
        consulting: 'Consulting & Strategy'
      }
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');

  useEffect(() => {
    const saved = localStorage.getItem('vegsoft-language');
    if (saved && (saved === 'es' || saved === 'en')) {
      setLanguage(saved);
    }
  }, []);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('vegsoft-language', lang);
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
